<template>
  <v-dialog
    v-model="show"
    persistent
    max-width="400"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <v-card-title class="error white--text">
        <v-icon left color="white" size="28">mdi-wifi-off</v-icon>
        <span class="text-h6">Problema de connexió</span>
      </v-card-title>
      
      <v-card-text class="pt-4">
        <div class="text-center mb-3">
          <v-icon 
            :color="getIconColor()" 
            size="48"
            class="mb-2"
          >
            {{ getIcon() }}
          </v-icon>
        </div>
        
        <p class="text-body-1 text-center mb-3">
          {{ errorMessage }}
        </p>
        
        <div v-if="retryCount > 0" class="text-center">
          <v-chip 
            small 
            :color="retryCount > 3 ? 'error' : 'warning'"
            text-color="white"
          >
            Intent {{ retryCount }} de 3
          </v-chip>
        </div>
        
        <!-- Indicador de connexió -->
        <div class="text-center mt-3">
          <v-chip 
            small 
            :color="isOnline ? 'success' : 'error'"
            text-color="white"
          >
            <v-icon left small>
              {{ isOnline ? 'mdi-wifi' : 'mdi-wifi-off' }}
            </v-icon>
            {{ isOnline ? 'Connexió detectada' : 'Sense connexió' }}
          </v-chip>
        </div>
      </v-card-text>
      
      <v-card-actions class="justify-center pb-4">
        <v-btn
          v-if="canRetry"
          color="primary"
          large
          @click="retry"
          :loading="retrying"
          :disabled="!isOnline"
          class="mr-2"
        >
          <v-icon left>mdi-refresh</v-icon>
          Tornar a provar
        </v-btn>
        
        <v-btn
          outlined
          large
          @click="dismiss"
          class="ml-2"
        >
          <v-icon left>mdi-close</v-icon>
          Tancar
        </v-btn>
      </v-card-actions>
      
      <!-- Consells basats en el tipus d'error -->
      <v-expansion-panels v-if="getTips().length > 0" flat>
        <v-expansion-panel>
          <v-expansion-panel-header class="py-2">
            <span class="text-caption">
              <v-icon small left>mdi-lightbulb-outline</v-icon>
              Consells per solucionar el problema
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ul class="text-caption">
              <li v-for="tip in getTips()" :key="tip" class="mb-1">
                {{ tip }}
              </li>
            </ul>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'ConnectionErrorDialog',
  data() {
    return {
      retrying: false,
      isOnline: navigator.onLine
    };
  },
  computed: {
    ...mapState({
      connectionError: state => state.connectionError
    }),
    show: {
      get() {
        return this.connectionError.show;
      },
      set(value) {
        if (!value) {
          this.$store.commit('hideConnectionError');
        }
      }
    },
    errorMessage() {
      return this.connectionError.message;
    },
    errorType() {
      return this.connectionError.type;
    },
    canRetry() {
      return this.connectionError.canRetry && this.retryCount < 3;
    },
    retryCount() {
      return this.connectionError.retryCount;
    }
  },
  mounted() {
    // Escoltar canvis en l'estat de la connexió
    window.addEventListener('online', this.updateOnlineStatus);
    window.addEventListener('offline', this.updateOnlineStatus);
  },
  beforeDestroy() {
    window.removeEventListener('online', this.updateOnlineStatus);
    window.removeEventListener('offline', this.updateOnlineStatus);
  },
  methods: {
    updateOnlineStatus() {
      this.isOnline = navigator.onLine;
    },
    getIcon() {
      switch (this.errorType) {
        case 'timeout':
          return 'mdi-timer-sand';
        case 'server':
          return 'mdi-server-network-off';
        case 'auth':
          return 'mdi-account-lock';
        case 'notfound':
          return 'mdi-file-question';
        default:
          return 'mdi-wifi-off';
      }
    },
    getIconColor() {
      switch (this.errorType) {
        case 'timeout':
          return 'orange';
        case 'server':
          return 'red';
        case 'auth':
          return 'purple';
        case 'notfound':
          return 'amber';
        default:
          return 'red';
      }
    },
    getTips() {
      switch (this.errorType) {
        case 'network':
          return [
            'Comprova que el WiFi o dades mòbils estiguin activats',
            'Intenta moure\'t a una zona amb millor cobertura',
            'Reinicia la connexió de xarxa del dispositiu'
          ];
        case 'timeout':
          return [
            'La connexió és lenta, espera uns segons',
            'Comprova la intensitat del senyal',
            'Tanca altres aplicacions que usin internet'
          ];
        case 'server':
          return [
            'El servidor pot estar temporalment no disponible',
            'Contacta amb l\'administrador si el problema persisteix'
          ];
        case 'auth':
          return [
            'La sessió ha caducat',
            'Torna a iniciar sessió a l\'aplicació'
          ];
        default:
          return [];
      }
    },
    async retry() {
      this.retrying = true;
      this.$store.commit('incrementRetryCount');
      
      try {
        // Esperar 1 segon abans de tancar el diàleg
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.$store.commit('hideConnectionError');
        
        // Recarregar la pàgina actual o repetir l'última acció
        this.$router.go(0);
      } catch (error) {
        console.error('Error en retry:', error);
      } finally {
        this.retrying = false;
      }
    },
    dismiss() {
      this.$store.commit('hideConnectionError');
    }
  }
};
</script>

<style scoped>
.v-dialog >>> .v-card {
  border-radius: 16px !important;
}

.v-dialog >>> .v-card__title {
  border-radius: 16px 16px 0 0 !important;
}

.text-caption {
  line-height: 1.4 !important;
}

.v-chip {
  font-weight: 500;
}
</style>
